import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { SharedComponentModule } from '@shared/component/component.module';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Company } from '@app/modules/company/models/company.model';
import { CompanyProfileDialogFormType, DialogData } from './types';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CompanyBasicInfoInput,
  SupportedCountryType,
} from '@generated/graphql';
import { Store } from '@ngrx/store';
import { supportedCountryStore } from '@app/modules/supported-country/store/supported-country.store';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CompanyApi } from '@app/modules/company/api/company.api';
import { DateTime } from 'luxon';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { SnackBarTypeEnum } from '@shared/types/commonEnum';

@Component({
  selector: 'app-create-company-profile-dialog',
  standalone: true,
  templateUrl: './create-company-profile-dialog.component.html',
  styleUrls: ['./create-company-profile-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    SharedComponentModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class CreateCompanyProfileDialogComponent implements OnInit, OnDestroy {
  company: Company;

  companyProfileDialogForm: FormGroup<CompanyProfileDialogFormType>;

  supportedCountries$: Observable<SupportedCountryType[]>;

  companyNames: string[];

  readonly ALTERNATIVE_NAME = 'ALTERNATIVE_NAME';

  showAlternativeInput = false;

  selectedName = '';

  submitting$ = new BehaviorSubject(false);

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<CreateCompanyProfileDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly store: Store,
    private readonly companyApi: CompanyApi,
    private readonly snackBarService: SnackBarService,
  ) {
    this.company = data.company;

    this.companyNames = [
      this.company.name,
      this.company.secondName,
      this.company.thirdName,
    ];
  }

  ngOnInit(): void {
    this.selectedName = this.company.name;

    this.supportedCountries$ = this.store.select(
      supportedCountryStore.selectAllEntity,
    );

    this.createForm();
  }

  showNameSelect(): boolean {
    if (this.company.secondName && this.company.thirdName) {
      return true;
    }

    return false;
  }

  private createForm(): void {
    this.companyProfileDialogForm = this.fb.group<CompanyProfileDialogFormType>(
      {
        name: new FormControl(this.selectedName, [Validators.required]),
        registrationNumber: new FormControl(this.company.registrationNumber, [
          Validators.required,
        ]),
        registrationDate: new FormControl(
          this.company.registrationDate
            ? DateTime.fromFormat(
              this.company.registrationDate.toString(),
              'yyyy-MM-dd HH:mm:ss',
            ).toJSDate()
            : null,
          [Validators.required],
        ),
        countryId: new FormControl(
          {
            value: this.company.country.id,
            disabled: true,
          },
          [Validators.required],
        ),
        website: new FormControl(this.company.website),
        numberOfEmployees: new FormControl(this.company.numberOfEmployees || 0),
        companyActivity: new FormControl(this.company.companyActivity, [
          Validators.required,
        ]),
      },
    );
  }

  onNameChanged(event: MatSelectChange): void {
    this.selectedName = event.value;

    if (event.value === this.ALTERNATIVE_NAME) {
      this.showAlternativeInput = true;
      this.companyProfileDialogForm.controls.name.setValue(null);
      this.companyProfileDialogForm.controls.name.updateValueAndValidity();
    } else {
      this.showAlternativeInput = false;

      this.companyProfileDialogForm.controls.name.setValue(this.selectedName);
    }
  }

  submitForm(): void {
    const input = Object.assign(
      {},
      this.companyProfileDialogForm.value as unknown as CompanyBasicInfoInput,
    );

    if (input.numberOfEmployees === null) {
      input.numberOfEmployees = 0;
    }

    input.registrationDate = DateTime.fromJSDate(
      input.registrationDate,
    ).toFormat('yyyy-MM-dd HH:mm:ss');

    this.submitting$.next(true);

    this.companyApi.createCompanyProfile(this.company.id, input).subscribe(
      (res) => {
        if (res === true) {
          this.snackBarService.pushAlert(
            'Company profile has been created!',
            null,
            SnackBarTypeEnum.success,
          );
          this.dialogRef.close(true);
        } else {
          this.snackBarService.pushErrorMessage();
        }
      },
      (error) => {
        console.error(error);
        this.snackBarService.pushErrorMessage();
        this.submitting$.next(false);
      },
      () => {
        this.submitting$.next(false);
      },
    );
  }

  ngOnDestroy(): void {
    this.submitting$.complete();
  }
}
